import React from 'react'
import { Modal, Button, Input, Label, Select, Textarea } from '@/shared/ui'
import { useReviewModal } from '../context/ReviewModalContext'
import { getOpportunityById, updateOpportunity, publishOpportunity, rejectOpportunity } from '../services/oportunidadesService'
import { useToast } from '@/shared/ui/Toast'

const TF_OPTIONS: { label: string; value: string }[] = [
  { label: '5min', value: '5' },
  { label: '15min', value: '15' },
  { label: '30min', value: '30' },
  { label: '1h', value: '60' },
  { label: '4h', value: '240' },
  { label: '12h', value: '720' },
  { label: 'Diário', value: 'D' },
  { label: 'Semanal', value: 'W' },
  { label: 'Mensal', value: 'M' },
]

export default function ReviewOpportunityModal({ open, onOpenChange, onAfterPublish }: { open: boolean; onOpenChange: (v: boolean)=>void; onAfterPublish?: ()=>void }) {
  const { item } = useReviewModal()
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [rejecting, setRejecting] = React.useState(false)

  const [title, setTitle] = React.useState('')
  const [trend, setTrend] = React.useState<'bullish'|'bearish'|''>('')
  const [timeframe, setTimeframe] = React.useState('')
  const [targetTimeframe, setTargetTimeframe] = React.useState('')
  const [opportunityType, setOpportunityType] = React.useState('')
  const [signalType, setSignalType] = React.useState('')
  const [summary, setSummary] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [cfRaw, setCfRaw] = React.useState<any>({})

  React.useEffect(() => {
    let mounted = true
    async function load() {
      if (!item?.id) return
      setLoading(true)
      try {
        const row = await getOpportunityById(item.id)
        if (!mounted || !row) return
        const cf = row.confluence_factors || {}
        setCfRaw(cf)
        // Fallbacks legíveis (título/summary/descrição) quando não houver
        const fallbackTitle = computeDefaultTitle(row)
        const fallbackSummary = computeDefaultSummary(row)
        const fallbackDescription = computeDefaultDescription(row)
        setTitle(String((cf.card_title || cf.title || row.title || fallbackTitle || '')))
        let t = String(row.target_trend || '').toLowerCase()
        if (!t) {
          const ot = String(row.opportunity_type || '').toUpperCase()
          const st = String(row.signal_type || '').toUpperCase()
          if (ot === 'OVERSOLD_BULLISH' || st === 'SEQUENTIAL-OVERSOLD-39') t = 'bullish'
          else if (ot === 'OVERBOUGHT_BEARISH' || st === 'SEQUENTIAL-OVERBOUGHT-61') t = 'bearish'
        }
        setTrend(t === 'bullish' || t === 'alta' ? 'bullish' : (t === 'bearish' || t === 'baixa' ? 'bearish' : ''))
        setTimeframe(String(row.timeframe || cf.timeframe_raw || ''))
        setTargetTimeframe(String(row.target_timeframe || cf.target_timeframe_raw || ''))
        setOpportunityType(String(row.opportunity_type || ''))
        setSignalType(String(row.signal_type || ''))
        setSummary(String((cf.card_summary || cf.summary || fallbackSummary || '')))
        setDescription(String((row.opportunity_description || cf.opportunity_description || cf.description || fallbackDescription || '')))
      } catch (e: any) {
        toast({ title: 'Revisão', description: e?.message || 'Falha ao carregar oportunidade', variant: 'error' })
      } finally {
        setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [item?.id])

  async function handleSave() {
    if (!item?.id) return
    setSaving(true)
    try {
      const cf = { ...cfRaw }
      // Persistir no JSON, pois a tabela não possui coluna 'title'
      cf.card_title = String(title || computeDefaultTitle({ ...cfRaw, ...{} })) || undefined
      if (summary) cf.card_summary = summary
      if (description) cf.opportunity_description = description
      if (timeframe) cf.timeframe_raw = timeframe
      if (targetTimeframe) cf.target_timeframe_raw = targetTimeframe
      await updateOpportunity(item.id, {
        timeframe: timeframe || null,
        target_timeframe: targetTimeframe || null,
        target_trend: trend || null,
        opportunity_type: opportunityType || null,
        signal_type: signalType || null,
        confluence_factors: cf,
      })
      toast({ title: 'Revisão', description: 'Alterações salvas.', variant: 'success', soft: true })
    } catch (e: any) {
      toast({ title: 'Revisão', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleReject() {
    if (!item?.id) return
    setRejecting(true)
    try {
      await rejectOpportunity(item.id)
      toast({ title: 'Revisão', description: 'Oportunidade rejeitada e encerrada.', variant: 'success' })
      try { window.dispatchEvent(new Event('opportunities:refresh')) } catch {}
      onAfterPublish?.()
      onOpenChange(false)
    } catch (e: any) {
      toast({ title: 'Revisão', description: e?.message || 'Falha ao rejeitar', variant: 'error' })
    } finally {
      setRejecting(false)
    }
  }

  async function handlePublish() {
    if (!item?.id) return
    await handleSave()
    try {
      await publishOpportunity(item.id)
      toast({ title: 'Revisão', description: 'Publicado com sucesso.', variant: 'success' })
      onAfterPublish?.()
      onOpenChange(false)
    } catch (e: any) {
      toast({ title: 'Revisão', description: e?.message || 'Falha ao publicar', variant: 'error' })
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} className="w-[900px] max-w-[95vw]">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">Revisar oportunidade</div>
        </div>
        {loading ? (
          <div className="text-sm text-foreground/70">Carregando...</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Título</Label>
              <Input value={title} onChange={(e)=> setTitle(e.target.value)} placeholder="Título do card" />
            </div>
            <div className="space-y-2">
              <Label>Tendência</Label>
              <Select value={trend} onChange={(e)=> setTrend(e.target.value as any)}>
                <option value="">—</option>
                <option value="bullish">Alta (bullish)</option>
                <option value="bearish">Baixa (bearish)</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>TF (sinal)</Label>
              <Select value={timeframe} onChange={(e)=> setTimeframe(String(e.target.value))}>
                <option value="">—</option>
                {TF_OPTIONS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label>TF (alvo)</Label>
              <Select value={targetTimeframe} onChange={(e)=> setTargetTimeframe(String(e.target.value))}>
                <option value="">—</option>
                {TF_OPTIONS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo da oportunidade</Label>
              <Select value={opportunityType} onChange={(e)=> setOpportunityType(e.target.value)}>
                <option value="">—</option>
                <option value="OVERSOLD_BULLISH">Alta (RSI 39 - Oversold)</option>
                <option value="OVERBOUGHT_BEARISH">Baixa (RSI 61 - Overbought)</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo do sinal</Label>
              <Select value={signalType} onChange={(e)=> setSignalType(e.target.value)}>
                <option value="">—</option>
                <option value="SEQUENTIAL-OVERSOLD-39">Sequencial RSI 39 (Alta)</option>
                <option value="SEQUENTIAL-OVERBOUGHT-61">Sequencial RSI 61 (Baixa)</option>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Resumo</Label>
              <Textarea rows={2} value={summary} onChange={(e)=> setSummary(e.target.value)} placeholder="Resumo curto do contexto" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Descrição</Label>
              <Textarea rows={3} value={description} onChange={(e)=> setDescription(e.target.value)} placeholder="Detalhes da oportunidade" />
            </div>
          </div>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button variant="destructive" onClick={handleReject} disabled={saving || loading || rejecting}>{rejecting ? 'Rejeitando...' : 'Rejeitar'}</Button>
          <Button variant="outline" onClick={handlePublish} disabled={saving || loading}>Publicar</Button>
          <Button onClick={handleSave} disabled={saving || loading}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </div>
    </Modal>
  )
}

function tfLabel(tfRaw?: string | null): string | null {
  const t = String(tfRaw || '').toUpperCase()
  if (!t) return null
  if (t === '5') return '5min'
  if (t === '15') return '15min'
  if (t === '30') return '30min'
  if (t === '60') return '1h'
  if (t === '240') return '4h'
  if (t === '720') return '12h'
  if (t === 'D') return 'Diário'
  if (t === 'W') return 'Semanal'
  if (t === 'M') return 'Mensal'
  return t
}

function computeDefaultTitle(row: any): string {
  try {
    const cf = (row?.confluence_factors || {})
    const trend = String(row?.target_trend ?? cf.trend_alvo ?? '').toLowerCase()
    const trendPt = trend === 'bearish' || trend === 'baixa' ? 'baixa' : trend ? 'alta' : ''
    const pivotRaw = String(cf.pivot_type ?? '').toLowerCase()
    let pivotLabel = ''
    if (pivotRaw.includes('ascending') || pivotRaw.includes('pivot_low') || (trendPt === 'alta' && !pivotRaw)) pivotLabel = 'fundo ascendente'
    if (pivotRaw.includes('descending') || pivotRaw.includes('pivot_high') || (trendPt === 'baixa' && !pivotRaw)) pivotLabel = 'topo descendente'
    const tfTarget = tfLabel(row?.target_timeframe ?? cf.target_timeframe_raw)
    const parts = [String((row?.symbol || '').toUpperCase()), trendPt ? `- Tendência de ${trendPt}:` : '', pivotLabel && tfTarget ? `buscando ${pivotLabel} no ${tfTarget}` : ''].filter(Boolean)
    return parts.join(' ') || String((row?.symbol || '')).toUpperCase()
  } catch { return String((row?.symbol || '')).toUpperCase() }
}

function computeDefaultSummary(row: any): string {
  try {
    const cf = (row?.confluence_factors || {})
    const trend = String(row?.target_trend ?? cf.trend_alvo ?? '').toLowerCase()
    const bullish = trend === 'bullish' || trend === 'alta'
    const tfTarget = tfLabel(row?.target_timeframe ?? cf.target_timeframe_raw)
    const tfSignal = tfLabel(row?.timeframe ?? cf.timeframe_raw)
    return bullish
      ? `Correção em curso; preferir pullback em região de Fibo/EMAs no ${tfTarget || 'alvo'} com RSI do ${tfSignal || 'sinal'} ajudando, buscando fundo ascendente.`
      : `Correção de baixa em curso; preferir pullback em resistências/EMAs no ${tfTarget || 'alvo'} com RSI do ${tfSignal || 'sinal'} pressionando, buscando topo descendente.`
  } catch { return '' }
}

function computeDefaultDescription(row: any): string {
  const cf = (row?.confluence_factors || {})
  return String(cf.opportunity_description || cf.description || '')
}
